import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Newsletter from '../components/Newsletter';
import Footer from '../components/Footer';

interface FAQItem {
  question: string;
  answer: string;
}

const EtudePosturale: React.FC = () => {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  const faqItems: FAQItem[] = [
    {
      question: "Combien de temps dure une étude posturale ?",
      answer: "Entre 1h30 et 2h30 selon la discipline et les réglages nécessaires."
    },
    {
      question: "Dois-je venir avec mon vélo ?",
      answer: "Oui, c'est l'idéal pour réaliser des réglages précis adaptés à ton matériel. (TDM : À adapter selon méthode utilisée)"
    },
    {
      question: "L'étude posturale règle-t-elle les douleurs ?",
      answer: "Dans la majorité des cas, oui. Elle permet d'identifier les causes posturales et d'ajuster la position pour soulager les zones sensibles."
    },
    {
      question: "Est-ce que vous ajoutez les cales ?",
      answer: "Oui, l'alignement et la rotation des cales font partie intégrante de l'étude."
    },
    {
      question: "À partir de quel niveau est-il utile de faire une étude posturale ?",
      answer: "Même un cycliste débutant bénéficiera d'une étude posturale. Plus tôt la position est optimisée, moins il y aura de risques de douleurs et de mauvaises habitudes. Pour les cyclistes réguliers, c'est essentiel pour progresser et pédaler efficacement."
    },
    {
      question: "Proposez-vous une analyse vidéo ou 3D pendant l'étude ?",
      answer: "Oui. L'analyse dynamique permet d'observer précisément les angles articulaires, le pédalage et la posture pour affiner les réglages au millimètre."
    },
    {
      question: "Est-il possible d'améliorer ma puissance grâce à l'étude posturale ?",
      answer: "Oui. Une position stable et optimisée permet un meilleur engagement musculaire, moins de pertes d'énergie et une meilleure transmission de la force dans les pédales."
    },
    {
      question: "Dois-je venir en tenue de vélo pour l'étude posturale ?",
      answer: "Oui, c'est indispensable de venir avec votre cuissard et vos chaussures afin de faire une analyse fidèle et des réglages conformes aux conditions réelles de votre pratique."
    },
    {
      question: "Y a-t-il une différence entre une étude posturale pour un cycliste route et un triathlète ?",
      answer: "Oui. Le cycliste route cherche un équilibre confort/performance, tandis que le triathlète vise l'aérodynamisme sans compromettre la puissance. Les réglages sont donc spécifiques."
    },
    {
      question: "Est-ce utile pour le gravel ?",
      answer: "Absolument : une bonne position améliore la stabilité, réduit la fatigue et diminue les impacts liés aux terrains mixtes. Elle vous aidera à soutenir des efforts d'endurance et de longues journées en bikepacking."
    },
    {
      question: "Combien de temps faut-il pour s'habituer à une nouvelle position ?",
      answer: "En général quelques sorties suffisent. Pour un changement important (nouvelle selle, changement de hauteur, prolongateurs), une adaptation de 1 à 3 semaines avec une pratique raisonnée peut être nécessaire le temps que le corps s'habitue aux nouveaux réglages."
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-12 md:pb-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center text-black mb-12 leading-tight">
            Étude posturale vélo à Paris : Bikefitting<br />
            route, triathlon et gravel
          </h1>

          {/* Main Image */}
          <div className="relative rounded-lg overflow-hidden mb-16 max-w-4xl mx-auto">
            <img
              src="/etudes posturale/SE-bikefit-2col-retul.jpg"
              alt="Cycliste sur home trainer - Étude posturale"
              className="w-full h-auto object-cover"
            />
          </div>
        </div>
      </section>

      {/* Content Section with Sidebar */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-black mb-4">
                  Une étude posturale vélo complète et personnalisée
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Notre <strong>étude posturale vélo à Paris</strong> s'adresse aux cyclistes route,
                  triathlètes et pratiquants gravel qui souhaitent améliorer leur position et leur confort,
                  optimiser leurs performances et prévenir les douleurs.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Grâce à un <strong>bikefitting complet et personnalisé</strong>, nous ajustons votre
                  vélo pour que vous ne fassiez qu'un avec votre machine, que vous soyez débutant ou
                  cycliste confirmé.
                </p>
              </div>

              <div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Situé tout proche de Paris (26 Bd Henri Sellier, 92150 Suresnes), dans une salle dédiée
                  au sein de notre boutique, l'<strong>étude posturale</strong> consiste en une étude dynamique sur home-
                  trainer (analyse vidéo et décomposition du mouvement).
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Nous réalisons votre bikefitting tout proche de <strong>Paris</strong> (26 Bd Henri Sellier, 92150
                  Suresnes), dans notre espace dédié, afin d'obtenir une <strong>analyse posturale vélo
                    complète et précise</strong>.
                </p>
              </div>

              <div>
                <p className="text-gray-700 leading-relaxed">
                  Réservez votre <strong>étude posturale vélo à Paris</strong> et repartez avec une
                  position précise, confortable et adaptée à votre pratique (route, gravel ou triathlon).
                </p>
              </div>

              {/* CTA Button */}
              <div className="pt-4">
                <Link
                  to="/reservation-etude-posturale"
                  className="inline-block bg-black text-white px-8 py-4 rounded-lg hover:bg-gray-800 transition-colors duration-300 font-medium text-lg"
                >
                  Je réserve mon étude posturale
                </Link>
              </div>
            </div>

            {/* Sidebar - Les Infos Essentielles */}
            <div className="lg:col-span-1">
              <div className="bg-gray-50 rounded-lg p-6 sticky top-24">
                <h3 className="text-xl font-bold text-black mb-6">Les Infos Essentielles</h3>
                <ul className="space-y-4 text-gray-700">
                  <li className="flex items-start">
                    <span className="mr-2">📍</span>
                    <span><strong>Lieu :</strong> 26 Bd Henri Sellier, 92150 Suresnes</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">⏱️</span>
                    <span><strong>Durée :</strong> Environ 2h</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">💰</span>
                    <span><strong>Prix :</strong> 250€</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">🔬</span>
                    <span><strong>Technique utilisée :</strong> À remplir</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">📅</span>
                    <span><strong>Disponibilités :</strong> Voir l'agenda (lien pour prendre rdv), ou nous contacter (mail + tel)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">⭐</span>
                    <span><strong>Les Avis :</strong> Ils nous font confiance (renvoi sur la section avis ils ont fait confiance à Podium pour leur étude posturale vélo)</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pourquoi faire son étude posturale */}
      <section className="py-12 md:py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Left Column - Pourquoi */}
            <div>
              <h2 className="text-2xl font-bold text-black mb-6">
                Pourquoi faire son étude posturale chez Podium
              </h2>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Expertise route / triathlon / gravel</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Analyse dynamique + réglages précis</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Expérience avec débutants & compétiteurs</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Conseils personnalisés</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Équipement professionnel</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Accompagnement après l'étude</span>
                </li>
              </ul>

              <Link
                to="/reservation-etude-posturale"
                className="inline-block mt-8 bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition-colors duration-300 font-medium"
              >
                Je prends rendez-vous
              </Link>
            </div>

            {/* Right Column - Étapes */}
            <div>
              <h2 className="text-2xl font-bold text-black mb-6">
                Étude posturale étape par étape
              </h2>
              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold text-black mb-2">1. Entretien sur vos attentes</h3>
                  <p className="text-gray-600 text-sm">
                    Un moment d'échange pour comprendre vos habitudes, vos douleurs éventuelles et ce que vous attendez de votre vélo.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-black mb-2">2. Étude dynamique sur home-trainer</h3>
                  <p className="text-gray-600 text-sm">
                    Vous pédalez pendant que nous analysons votre posture et votre coup de pédale en conditions réelles. Une méthode précise et 100% personnalisée.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-black mb-2">3. Réglages sur-mesure</h3>
                  <p className="text-gray-600 text-sm">
                    Hauteur et recul de selle, longueur de potence, largeur du cintre et position des leviers, ajustement des cales. Nous optimisons chaque point pour aligner confort, stabilité et efficacité.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-black mb-2">4. Validation & ajustements fins</h3>
                  <p className="text-gray-600 text-sm">
                    Nous ajustons jusqu'à ce que vous vous sentiez en phase avec votre vélo. Le but : trouver la position parfaitement adaptée.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-black mb-2">5. Un rapport complet et clair</h3>
                  <p className="text-gray-600 text-sm">
                    Vous repartez avec tous vos réglages, des conseils personnalisés et de vraies recommandations pour continuer à progresser.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Images Section */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="rounded-lg overflow-hidden">
              <img
                src="/etudes posturale/e439a3c8d6ca0e86ccba532a0a8733e3.jpg"
                alt="Étude posturale - Analyse dynamique"
                className="w-full h-64 md:h-80 object-cover"
              />
            </div>
            <div className="rounded-lg overflow-hidden">
              <img
                src="/etudes posturale/870369e066a0630535c77ab40541d12c.jpg"
                alt="Étude posturale - Bikefitting"
                className="w-full h-64 md:h-80 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Confiance Section - Avis Clients */}
      <section className="py-12 md:py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-black mb-4">
              Ils ont fait confiance à Podium pour leur étude posturale vélo
            </h2>
            <div className="flex items-center justify-center gap-2 mb-2">
              <div className="flex">
                {[1, 2, 3, 4, 5].map((star) => (
                  <svg key={star} className="w-6 h-6 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <span className="text-xl font-bold text-black">4.9</span>
              <span className="text-gray-500">sur Google</span>
            </div>
          </div>

          {/* Avis Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Avis 1 */}
            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  M
                </div>
                <div>
                  <h4 className="font-semibold text-black">Mathieu L.</h4>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <svg key={star} className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                    <span className="text-xs text-gray-400 ml-2">il y a 2 semaines</span>
                  </div>
                </div>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed">
                "Excellente étude posturale ! J'avais des douleurs au genou depuis des mois et après les réglages de Thomas, tout a disparu. Le rapport détaillé est un vrai plus. Je recommande à 100%."
              </p>
            </div>

            {/* Avis 2 */}
            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-rose-500 to-rose-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  S
                </div>
                <div>
                  <h4 className="font-semibold text-black">Sophie D.</h4>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <svg key={star} className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                    <span className="text-xs text-gray-400 ml-2">il y a 1 mois</span>
                  </div>
                </div>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed">
                "En préparation de mon premier triathlon, j'ai fait appel à Podium pour optimiser ma position. Résultat : +15W sur mon test de puissance et un confort incroyable sur les longues distances. Merci !"
              </p>
            </div>

            {/* Avis 3 */}
            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  P
                </div>
                <div>
                  <h4 className="font-semibold text-black">Pierre M.</h4>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <svg key={star} className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                    <span className="text-xs text-gray-400 ml-2">il y a 3 semaines</span>
                  </div>
                </div>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed">
                "Cycliste depuis 10 ans, je pensais que ma position était bonne. L'étude posturale m'a prouvé le contraire ! Les réglages sont précis et l'équipe vraiment professionnelle. À faire absolument."
              </p>
            </div>

            {/* Avis 4 */}
            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  J
                </div>
                <div>
                  <h4 className="font-semibold text-black">Julie R.</h4>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star, idx) => (
                      <svg key={star} className={`w-4 h-4 ${idx < 5 ? 'text-yellow-400' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                    <span className="text-xs text-gray-400 ml-2">il y a 1 semaine</span>
                  </div>
                </div>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed">
                "Super expérience chez Podium ! L'analyse vidéo est vraiment impressionnante, on voit clairement les défauts de pédalage. Ma position gravel est maintenant parfaite pour mes longues sorties."
              </p>
            </div>

            {/* Avis 5 */}
            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  A
                </div>
                <div>
                  <h4 className="font-semibold text-black">Antoine B.</h4>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <svg key={star} className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                    <span className="text-xs text-gray-400 ml-2">il y a 2 mois</span>
                  </div>
                </div>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed">
                "Après mon achat vélo chez Podium, j'ai enchaîné avec l'étude posturale. Le combo parfait ! Position optimisée dès le premier jour, zéro douleur sur ma première sortie de 100km."
              </p>
            </div>

            {/* Avis 6 */}
            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  C
                </div>
                <div>
                  <h4 className="font-semibold text-black">Christophe V.</h4>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <svg key={star} className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                    <span className="text-xs text-gray-400 ml-2">il y a 1 mois</span>
                  </div>
                </div>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed">
                "Bikefitting de qualité professionnelle. L'équipe prend le temps d'expliquer chaque ajustement. Mon mal de dos chronique a enfin disparu. Investissement rentabilisé en quelques sorties !"
              </p>
            </div>
          </div>

          {/* Google Badge */}
          <div className="mt-10 flex justify-center">
            <button
              onClick={() => window.open('https://www.google.com/maps', '_blank')}
              className="inline-flex items-center gap-2 bg-white px-6 py-3 rounded-full shadow-sm hover:shadow-md transition-all text-gray-700 font-medium cursor-pointer"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Voir tous nos avis sur Google
            </button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-black mb-12">FAQ</h2>

          <div className="space-y-4">
            {faqItems.map((item, index) => (
              <div key={index} className="border-b border-gray-200 pb-4">
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full text-left flex justify-between items-center py-2 hover:text-gray-600 transition-colors"
                >
                  <span className="font-medium text-black">{item.question}</span>
                  <svg
                    className={`w-5 h-5 transform transition-transform ${openFAQ === index ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {openFAQ === index && (
                  <p className="text-gray-600 mt-2 text-sm leading-relaxed">
                    {item.answer}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <Newsletter />
      <Footer />
    </div>
  );
};

export default EtudePosturale;
